/** Local Error Boundary for the Canvas "编辑图片" image editor overlay
 * (VistoeraTASK-00103 — Canvas Image Editor Stability / intermittent
 * "画布加载失败" root-cause fix).
 *
 * FORENSIC FINDING this exists to contain (see `DualPaneCutoutEditor.tsx`'s
 * `pushHistory`/`invertSelection` doc comments for the actual root cause
 * this task fixed): before this task, `CanvasImageEditorOverlay` had NO
 * local error boundary anywhere above it. `ProductionCanvasPage.tsx` wraps
 * the entire `CanvasBoard` (every node on the whole Canvas) in exactly ONE
 * `CanvasErrorBoundary` — so ANY uncaught exception thrown while the image
 * editor's Modal/canvas/Color-Picker/brush tree was mounted (this task
 * found and fixed one concrete, reliably-reachable one: `getImageData()`
 * throwing `IndexSizeError` on the still-0×0 offscreen canvas whenever a
 * pixel-touching interaction landed before the source image finished
 * loading — see the shared core's fix) bubbled all the way past this one
 * node's editor, past every other Canvas node, and took down the entire
 * Canvas Surface with the generic "画布加载失败" / `canvas-xxxxxxx-xxxxxx`
 * fatal page. `window.location.reload()` was the only recovery.
 *
 * This boundary is the architectural half of the fix (the root-cause data
 * guard above is the other half, and is what actually prevents the crash
 * from happening in the first place) — it exists so any OTHER, still-
 * unknown exception in this editor's tree is contained locally from now
 * on, exactly per this task's isolation requirement:
 *   - Canvas root (`CanvasErrorBoundary`) is never touched by an editor
 *     failure — it stays reserved for genuinely fatal Canvas-wide errors.
 *   - This node, every other Canvas node, edges, viewport, selection, and
 *     any unsaved autosave state are all completely unaffected — this
 *     boundary's fallback replaces ONLY the editor Modal's own subtree.
 *   - The tenant sees a plain, local, non-technical message + a
 *     diagnostic reference id (never a raw stack/exception) — the raw
 *     `error`/`componentStack` only ever go to `console.error` (P68/P69 —
 *     traceable in the browser console tagged with the SAME id shown to
 *     the user; never logs a URL query string / signed-URL secret).
 *   - "重试" ONLY remounts a fresh editor instance (the caller does this
 *     by bumping a `key` on this boundary itself — see `ImageAssetNode.tsx`
 *     — which resets both this boundary's own state AND every ref/state
 *     inside the crashed editor subtree; never `window.location.reload()`,
 *     never touches the rest of the Canvas).
 *   - "关闭编辑器" just calls the same `onClose` the editor's own header
 *     "返回 Canvas" button already uses — back to a perfectly normal,
 *     fully interactive Canvas underneath.
 */

import { Button, Modal, Result } from 'antd'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { withTranslation, type WithTranslation } from 'react-i18next'
import { MODAL_SURFACE_CLASS } from '../../../../utils/scrollLock'
import {
  createEditorDiagnosticsSnapshot,
  isDynamicImportChunkFailure,
  type EditorDiagnosticsRef,
} from '../../../../../components/editor/editorDiagnostics'

interface Props extends WithTranslation {
  children: ReactNode
  /** Same contract as `CanvasImageEditorOverlayProps.onClose` — unmounts
   * the editor overlay entirely, back to a plain (already-saved,
   * untouched) Canvas node. */
  onClose: () => void
  /** Caller bumps a `key` on this boundary (see `ImageAssetNode.tsx`) in
   * response to this — a full remount, never a partial internal reset,
   * so a genuinely fresh editor session (fresh refs/state/effects, no
   * chance of a stale async completion from the crashed instance ever
   * touching the new one) starts from a completely neutral state. */
  onRetry: () => void
  /** VistoeraTASK-00111 P4/P7/P13-P17 — the SAME mutable diagnostics box
   * `ImageAssetNode` also threads down into `CanvasImageEditorOverlay`/
   * `DualPaneCutoutEditor`. Optional: an `ImageEditorErrorBoundary` still
   * works (just with a smaller diagnostic log) if a caller ever omits it. */
  diagnosticsRef?: EditorDiagnosticsRef | null
}

/** VistoeraTASK-00111 P58 — a small, closed classification, not a guess:
 * every value here is derived from either the thrown `Error`'s own
 * `name`/`message` or from `diagnosticsRef` state this boundary's own
 * children maintain — never fabricated. */
type ErrorKind = 'DYNAMIC_IMPORT_CHUNK_FAILURE' | 'RUNTIME_CRASH'

interface State {
  errorId: string | null
  errorKind: ErrorKind | null
}

/** `imgedit-` prefix (vs. root `CanvasErrorBoundary`'s `canvas-` prefix) —
 * P69: distinguishable at a glance in a support conversation/console log
 * as "an Image Editor-local failure", never confusable with a genuine
 * Canvas-root fatal error. Same `Date.now` + random suffix shape.
 *
 * VistoeraTASK-00111 P11 — a fresh id every single time this boundary
 * catches (React calls `getDerivedStateFromError` again on every new
 * catch, including after a "重试" remount that itself crashes again), so
 * two distinct crash instances never share one reference id. */
function makeEditorErrorId(): string {
  return `imgedit-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

class ImageEditorErrorBoundary extends Component<Props, State> {
  state: State = { errorId: null, errorKind: null }

  static getDerivedStateFromError(error: Error): State {
    return {
      errorId: makeEditorErrorId(),
      errorKind: isDynamicImportChunkFailure(error) ? 'DYNAMIC_IMPORT_CHUNK_FAILURE' : 'RUNTIME_CRASH',
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // VistoeraTASK-00111 P4/P7/P10/P14 — upgrades `imgedit-*` from "a
    // random reference with zero diagnostic value" (P4's own finding
    // about this exact boundary) to a structured, `console.error`-logged
    // snapshot a developer can grep for by that SAME id. P6/P8 — this is
    // the ONLY place any of this detail is written; the user-facing
    // Modal below never renders `error`/`componentStack`/this object.
    const diagnostics = this.props.diagnosticsRef?.current ?? createEditorDiagnosticsSnapshot()
    const componentStackTop = (info.componentStack ?? '').trim().split('\n')[0]?.trim() ?? null
    const stackTop = (error.stack ?? '').trim().split('\n')[0]?.trim() ?? null
    // eslint-disable-next-line no-console
    console.error('[ImageEditorErrorBoundary]', {
      error_id: this.state.errorId,
      error_kind: this.state.errorKind,
      error_name: error?.name ?? null,
      error_message: error?.message ?? null,
      // Full stack/componentStack ARE logged (developer-only console,
      // never rendered in the UI, P6) — `_top` fields duplicated above
      // purely so a quick glance/grep does not require expanding the
      // full multi-line value.
      error_stack_top: stackTop,
      component_stack_top: componentStackTop,
      editor_phase_at_crash: diagnostics.phase,
      source_kind: diagnostics.sourceKind,
      source_ready: diagnostics.sourceReady,
      pixels_ready: diagnostics.pixelsReady,
      source_natural_width: diagnostics.sourceNaturalWidth,
      source_natural_height: diagnostics.sourceNaturalHeight,
      image_canvas_width: diagnostics.imageCanvasWidth,
      image_canvas_height: diagnostics.imageCanvasHeight,
      mask_canvas_width: diagnostics.maskCanvasWidth,
      mask_canvas_height: diagnostics.maskCanvasHeight,
      left_view_canvas_width: diagnostics.leftViewCanvasWidth,
      left_view_canvas_height: diagnostics.leftViewCanvasHeight,
      right_view_canvas_width: diagnostics.rightViewCanvasWidth,
      right_view_canvas_height: diagnostics.rightViewCanvasHeight,
      active_tool: diagnostics.activeTool,
      last_source_error_class: diagnostics.lastSourceErrorClass,
      editor_mount_generation: diagnostics.editorMountGeneration,
      sensitive_data_logged: false,
      full_error: error,
      full_component_stack: info.componentStack,
    })
  }

  private handleRetry = () => {
    this.props.onRetry()
  }

  private handleClose = () => {
    this.props.onClose()
  }

  /** VistoeraTASK-00111 P30-P32 — a stale dynamically-imported chunk after
   * a redeploy cannot be fixed by remounting the SAME component tree
   * ("重试" retries the identical, still-missing chunk URL and will
   * predictably fail again) — only a fresh `index.html` (i.e. an actual
   * page reload) picks up the current chunk graph. This is a distinct,
   * EXPLICIT user-initiated action, never automatic — does not violate
   * "`ImageEditorErrorBoundary` must never reload the whole page [on its
   * own]" (see this file's own top doc comment / TASK-00103's test),
   * exactly as `CanvasErrorBoundary`'s own reload button is also an
   * explicit click, never triggered by the boundary itself. */
  private handleReloadPage = () => {
    window.location.reload()
  }

  render() {
    const { t } = this.props
    if (this.state.errorId) {
      const isChunkFailure = this.state.errorKind === 'DYNAMIC_IMPORT_CHUNK_FAILURE'
      return (
        <Modal
          open
          onCancel={this.handleClose}
          footer={null}
          closable={false}
          width={420}
          centered
          wrapClassName={MODAL_SURFACE_CLASS}
          data-testid="image-editor-error-boundary"
        >
          <Result
            status="error"
            title={t('imageEditor.crashTitle')}
            subTitle={
              isChunkFailure
                ? `${t('imageEditor.crashErrorId', { id: this.state.errorId })} ${t('imageEditor.crashStaleVersionHint')}`
                : t('imageEditor.crashErrorId', { id: this.state.errorId })
            }
            extra={[
              <Button key="close" onClick={this.handleClose} data-testid="image-editor-error-boundary-close">
                {t('imageEditor.crashClose')}
              </Button>,
              <Button key="retry" type="primary" onClick={this.handleRetry} data-testid="image-editor-error-boundary-retry">
                {t('imageEditor.crashRetry')}
              </Button>,
              isChunkFailure ? (
                <Button key="reload" onClick={this.handleReloadPage} data-testid="image-editor-error-boundary-reload-page">
                  {t('imageEditor.crashReloadPage')}
                </Button>
              ) : null,
            ]}
          />
        </Modal>
      )
    }
    return this.props.children
  }
}

export default withTranslation('canvas')(ImageEditorErrorBoundary)
